import { useCallback, useState } from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import type { CleaningReport, FloorPlanSuggestions, FloorPlanValidation } from '../models/floorplan-automation';
import {
  cleanFloorPlanMetadata,
  getFloorPlanSuggestions,
  getFloorPlanValidation,
  type AutomationFailure,
} from '../services/floorplan-automation.service';

export interface AutomationState {
  readonly validation?: FloorPlanValidation;
  readonly suggestions?: FloorPlanSuggestions;
  /** The last dry run's report, which is what the Apply button acts on. */
  readonly cleaning?: CleaningReport;
  readonly busy: boolean;
  readonly error?: AutomationFailure;
  readonly runValidation: () => Promise<void>;
  readonly runSuggestions: () => Promise<void>;
  /** Asks what cleaning would do. Writes nothing. */
  readonly previewCleaning: () => Promise<void>;
  /** Writes. Returns whether anything changed, so the caller can reload the plan. */
  readonly applyCleaning: () => Promise<boolean>;
  /** Drops everything fetched, for when the plan underneath has changed. */
  readonly reset: () => void;
}

/**
 * The automation, on demand.
 *
 * Nothing is fetched until it is asked for. Validation walks every feature of every layer
 * and detection is size-matching over the whole floor drawing, and neither is work worth
 * doing while an admin is dragging a desk — so both run when the panel is opened, not when
 * the editor is.
 *
 * Holds no opinion about what to do with the answers: applying a suggestion goes through the
 * editor's document so it lands in the undo stack, and this hook never touches it.
 */
export const useFloorPlanAutomation = (floorPlanId: string | undefined): AutomationState => {
  const keycloak = useKeycloak();
  const [validation, setValidation] = useState<FloorPlanValidation | undefined>();
  const [suggestions, setSuggestions] = useState<FloorPlanSuggestions | undefined>();
  const [cleaning, setCleaning] = useState<CleaningReport | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AutomationFailure | undefined>();

  const reset = useCallback((): void => {
    setValidation(undefined);
    setSuggestions(undefined);
    setCleaning(undefined);
    setError(undefined);
  }, []);

  const runValidation = useCallback(async (): Promise<void> => {
    if (!floorPlanId) {
      return;
    }

    setBusy(true);
    const result = await getFloorPlanValidation(floorPlanId, keycloak);
    setBusy(false);

    if (result.status === 'ok') {
      setValidation(result.data);
      setError(undefined);
      return;
    }

    // The previous report is dropped rather than left on screen: a stale list of issues
    // beside a failed refresh reads as "these are current", which is the one thing it is not.
    setValidation(undefined);
    setError(result.status);
  }, [floorPlanId, keycloak]);

  const runSuggestions = useCallback(async (): Promise<void> => {
    if (!floorPlanId) {
      return;
    }

    setBusy(true);
    const result = await getFloorPlanSuggestions(floorPlanId, keycloak);
    setBusy(false);

    if (result.status === 'ok') {
      setSuggestions(result.data);
      setError(undefined);
      return;
    }

    setSuggestions(undefined);
    setError(result.status);
  }, [floorPlanId, keycloak]);

  const previewCleaning = useCallback(async (): Promise<void> => {
    if (!floorPlanId) {
      return;
    }

    setBusy(true);
    const result = await cleanFloorPlanMetadata(floorPlanId, keycloak, true);
    setBusy(false);

    if (result.status === 'ok') {
      setCleaning(result.data);
      setError(undefined);
      return;
    }

    setCleaning(undefined);
    setError(result.status);
  }, [floorPlanId, keycloak]);

  const applyCleaning = useCallback(async (): Promise<boolean> => {
    if (!floorPlanId) {
      return false;
    }

    setBusy(true);
    const result = await cleanFloorPlanMetadata(floorPlanId, keycloak, false);
    setBusy(false);

    if (result.status !== 'ok') {
      setError(result.status);
      return false;
    }

    // Replaced with the report of what was actually written, so the panel stops offering to
    // apply something that has been applied.
    setCleaning(result.data);
    setError(undefined);

    return result.data?.changedAnything ?? false;
  }, [floorPlanId, keycloak]);

  return {
    validation,
    suggestions,
    cleaning,
    busy,
    error,
    runValidation,
    runSuggestions,
    previewCleaning,
    applyCleaning,
    reset,
  };
};

export default useFloorPlanAutomation;
